import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  DollarSign,
  Music2,
  TrendingUp,
  Crown,
  Mic,
  Film,
  Wand2,
  Megaphone,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";
import Seo from "@/components/Seo";

const services = [
  {
    icon: ShieldCheck,
    name: "Publishing Establishment",
    code: "OWN",
    summary: "Your publishing side, established properly — the first time.",
    bullets: [
      "Publishing affiliation setup, done right",
      "Songwriter & publisher entity setup",
      "Full catalog work registration with proper metadata",
      "Split sheets, co-writer agreements & sample clearances",
    ],
  },
  {
    icon: DollarSign,
    name: "Royalty Recovery & Collection",
    code: "COLLECT",
    summary: "Mechanical · Performance · Sync · Streaming · Neighbouring.",
    bullets: [
      "Audit of historical earnings across every income source",
      "Mechanical royalties collected domestically and internationally",
      "Performance royalties collected across every eligible society",
      "Neighbouring rights recovery — domestic + international",
    ],
  },
  {
    icon: TrendingUp,
    name: "Royalty Tracking Dashboard",
    code: "TRACK",
    summary: "A live view of every dollar you've earned and what's owed.",
    bullets: [
      "Real-time portal with platform-level breakdowns",
      "Track pending vs received vs paid-out income",
      "Forecasting and quarterly statements",
      "Document vault for splits, royalty statements & contracts",
    ],
  },
  {
    icon: Music2,
    name: "Distribution & Placement",
    code: "MONETIZE",
    summary: "Placement on every major streaming and download platform.",
    bullets: [
      "Distribution to every major platform with proper metadata",
      "Editorial playlist pitching across genres",
      "Sync-licensing opportunities in TV, film, ads & games",
      "Curator outreach & algorithmic positioning",
    ],
  },
  {
    icon: Film,
    name: "Signature Performance Videos",
    code: "AMPLIFY",
    summary: "Cinematic performance visuals built around your sound.",
    bullets: [
      "Custom concept built around your lyrics and energy",
      "Signature performance visuals, characters and worlds",
      "Delivered ready for every major platform",
      "Two rounds of revisions",
    ],
  },
  {
    icon: Wand2,
    name: "Artist Mythology / Brand Story",
    code: "AMPLIFY",
    summary: "Your origin story, weaponised.",
    bullets: [
      "Founding narrative & artist positioning document",
      "Visual identity system (logo, palette, typography)",
      "Hero imagery & 10 lifestyle assets",
      "Press kit (EPK), bio variants & one-pager",
    ],
  },
  {
    icon: Megaphone,
    name: "Marketing Subscription",
    code: "AMPLIFY",
    summary: "Always-on growth — content, ads, rollouts, pitches.",
    bullets: [
      "AI-assisted social captions & post calendar",
      "Release rollout plans for every drop",
      "Ad creative + targeting briefs for Meta / TikTok",
      "Curator pitch emails for editorial playlists",
    ],
  },
  {
    icon: Crown,
    name: "Career Consulting",
    code: "OWN",
    summary: "Strategy sessions for releases, branding, deals & ownership.",
    bullets: [
      "Release strategy & rollout planning",
      "Brand & visual identity guidance",
      "Deal review (publishing, distribution, 360, sync agreements)",
      "Tour, merch & ancillary revenue strategy",
    ],
  },
  {
    icon: Mic,
    name: "Aspiring Artist Bootcamp",
    code: "OWN",
    summary: "A foundational program for artists at the starting line.",
    bullets: [
      "Music business 101 — rights, royalties, ownership",
      "DIY publishing workflow & PRO selection",
      "How to register works, splits & samples correctly",
      "Live Q&A and 1:1 consultation calls",
    ],
  },
];

const codeColor = {
  OWN: "border-[var(--primary)]/40 text-[var(--primary)] bg-[var(--primary)]/5",
  COLLECT: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  TRACK: "border-cyan-500/40 text-cyan-300 bg-cyan-500/5",
  MONETIZE: "border-amber-400/40 text-amber-300 bg-amber-400/5",
  AMPLIFY: "border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/5",
};

export default function Services() {
  return (
    <div className="bg-[#050505] text-white">
      <Seo
        title="Services — Own, Collect, Track, Monetize, Amplify"
        description="Full-service artist infrastructure: publishing registration, royalty recovery, catalog administration, distribution alignment, sync placement, and brand/performance visuals."
        path="/services"
      />
      <section className="border-b border-white/10 py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <div className="tag mb-6">// The framework</div>
            <h1 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter">
              Own. Collect.
              <br />
              Track. Monetize.
              <br />
              <span className="text-[var(--primary)]">Amplify.</span>
            </h1>
          </div>
          <div className="md:col-span-4 flex items-end">
            <p className="text-zinc-400 text-base leading-relaxed">
              Five operating systems. One outcome. Your music becomes a brand
              you own, with money you can see, on platforms that pay — visually
              amplified for the world.
            </p>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-12 flex flex-wrap items-center gap-3">
          {["OWN", "COLLECT", "TRACK", "MONETIZE", "AMPLIFY"].map((c) => (
            <span
              key={c}
              className={`px-3 py-1.5 border font-mono text-[10px] tracking-[0.22em] uppercase ${codeColor[c]}`}
            >
              {c}
            </span>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 gap-px bg-white/10">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.name}
                  className="bg-[#0a0a0a] p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 card-hover"
                >
                  <div className="md:col-span-1 flex md:flex-col items-center md:items-start gap-4">
                    <span
                      className={`px-2.5 py-1 border font-mono text-[10px] tracking-[0.22em] uppercase ${codeColor[s.code]}`}
                    >
                      {s.code}
                    </span>
                    <div className="w-12 h-12 border border-[var(--primary)]/40 bg-[var(--primary)]/5 flex items-center justify-center text-[var(--primary)]">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="md:col-span-5">
                    <h3 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mb-3">
                      {s.name}
                    </h3>
                    <p className="text-[var(--primary)] text-sm font-mono tracking-wide">
                      {s.summary}
                    </p>
                  </div>
                  <div className="md:col-span-6">
                    <ul className="space-y-3">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-zinc-300 text-sm leading-relaxed">
                          <CheckCircle2
                            size={16}
                            className="text-[var(--primary)] mt-0.5 flex-shrink-0"
                            strokeWidth={1.5}
                          />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10 text-center">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mb-6">
            Not sure where you fit?
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto mb-10">
            Book a free 30-minute consultation. We&apos;ll diagnose where
            you&apos;re losing money — and where your story isn&apos;t being told.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/booking" className="btn-primary inline-flex">
              Book a free consultation <ArrowUpRight size={16} />
            </Link>
            <Link to="/pricing" className="btn-ghost inline-flex">
              See pricing <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
